import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, theme } from 'antd'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { ChangeRow } from './components/ChangeRow'
import { LoadCard } from './components/LoadCard'
import { CARD_STYLE } from './styles'
import type { OrderChangeSummaryCardProps } from './types'
import {
  computeDropChanges,
  formatDeliveryTime,
  formatTimestamp,
  getMappingDisplay,
  hasDeliveryWindowChange,
  matchDrops,
} from './utils'

// Helper to check if a value is empty/null/undefined
const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === '—'

// Helper to check if a change row should be shown
const hasValue = (before: unknown, after: unknown): boolean =>
  !isEmpty(before) || !isEmpty(after)

export function OrderChangeSummaryCard({
  beforeChange,
  afterChange,
  timestamp,
}: OrderChangeSummaryCardProps) {
  const [expanded, setExpanded] = useState(false)
  const { token } = theme.useToken()

  // Early return if no data
  if (!beforeChange && !afterChange) return null

  // Check what types of changes exist
  const hasDeliveryWindow = hasDeliveryWindowChange(
    beforeChange?.delivery_window,
    afterChange?.delivery_window,
  )

  // Check if any drops have changes
  const matchedDrops = matchDrops(beforeChange?.drops, afterChange?.drops)
  const hasDropChanges = matchedDrops.some((matched) => {
    const changes = computeDropChanges(matched.before, matched.after)
    return changes.totalCount > 0
  })

  const hasNote = beforeChange?.note !== afterChange?.note

  // Don't show if there's nothing to display
  const hasChangeDetails = hasDeliveryWindow || hasDropChanges || hasNote
  if (!hasChangeDetails) return null

  const formattedTime = formatTimestamp(timestamp)

  return (
    <Card
      size='small'
      style={{
        ...CARD_STYLE,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      }}
      styles={{
        body: { padding: 12 },
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Vertical gap={8}>
        {/* Header Row: Change Summary + Timestamp + Chevron */}
        <Horizontal justifyContent='space-between' alignItems='center'>
          <Texto weight='600'>Change Summary</Texto>
          <Horizontal gap={8} alignItems='center'>
            {formattedTime && (
              <Texto appearance='hint' style={{ fontSize: 12 }}>
                {formattedTime}
              </Texto>
            )}
            {expanded ? (
              <ChevronUp size={16} color={token.colorTextTertiary} />
            ) : (
              <ChevronDown size={16} color={token.colorTextTertiary} />
            )}
          </Horizontal>
        </Horizontal>

        {/* Expanded Content */}
        {expanded && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              marginTop: 12,
              paddingTop: 12,
              cursor: 'default',
            }}
          >
            <Vertical gap={16}>
              {/* Delivery Window Section */}
              {hasDeliveryWindow && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Delivery Window
                  </Texto>
                  {(() => {
                    const beforeStart = beforeChange?.delivery_window
                      ? formatDeliveryTime(beforeChange.delivery_window.start)
                      : null
                    const beforeEnd = beforeChange?.delivery_window
                      ? formatDeliveryTime(beforeChange.delivery_window.end)
                      : null
                    const afterStart = afterChange?.delivery_window
                      ? formatDeliveryTime(afterChange.delivery_window.start)
                      : null
                    const afterEnd = afterChange?.delivery_window
                      ? formatDeliveryTime(afterChange.delivery_window.end)
                      : null

                    const formatDT = (
                      t: { date: string; time: string } | null,
                    ) => (t ? `${t.date} ${t.time}` : '—')

                    const startChanged =
                      formatDT(beforeStart) !== formatDT(afterStart)
                    const endChanged =
                      formatDT(beforeEnd) !== formatDT(afterEnd)

                    return (
                      <Vertical gap={4}>
                        {startChanged && (
                          <ChangeRow
                            label='Start'
                            beforeValue={formatDT(beforeStart)}
                            afterValue={formatDT(afterStart)}
                          />
                        )}
                        {endChanged && (
                          <ChangeRow
                            label='End'
                            beforeValue={formatDT(beforeEnd)}
                            afterValue={formatDT(afterEnd)}
                          />
                        )}
                      </Vertical>
                    )
                  })()}
                </Vertical>
              )}

              {/* Drops Section */}
              {matchedDrops
                .map((matched, index) => {
                  const changes = computeDropChanges(
                    matched.before,
                    matched.after,
                  )
                  if (changes.totalCount === 0) return null

                  const siteName =
                    getMappingDisplay(matched.after?.site) ||
                    getMappingDisplay(matched.before?.site) ||
                    'Unknown Site'

                  return (
                    <Vertical key={index} gap={8}>
                      <Texto weight='600' style={{ fontSize: 13 }}>
                        Drop {index + 1} — {siteName}
                      </Texto>

                      {/* Destination changes */}
                      {changes.destination
                        .filter((change) =>
                          hasValue(change.before, change.after),
                        )
                        .map((change, i) => (
                          <ChangeRow
                            key={`dest-${i}`}
                            label={change.field}
                            beforeValue={change.before}
                            afterValue={change.after}
                          />
                        ))}

                      {/* Product & Volume changes */}
                      {changes.productVolume
                        .filter((change) =>
                          hasValue(change.before, change.after),
                        )
                        .map((change, i) => (
                          <ChangeRow
                            key={`pv-${i}`}
                            label={change.field}
                            beforeValue={change.before}
                            afterValue={change.after}
                          />
                        ))}

                      {/* Loads Section */}
                      {(() => {
                        const beforeLoads = matched.before?.loads || []
                        const afterLoads = matched.after?.loads || []
                        const maxLen = Math.max(
                          beforeLoads.length,
                          afterLoads.length,
                        )

                        if (maxLen === 0) return null

                        // Build a lookup for load changes by index
                        const loadChangesMap = new Map(
                          changes.loads.map((lc) => [lc.loadIndex, lc.fields]),
                        )

                        // Get drop-level product as fallback
                        const dropProduct =
                          getMappingDisplay(matched.after?.product) ||
                          getMappingDisplay(matched.before?.product)

                        return (
                          <Vertical gap={8}>
                            <Texto
                              weight='600'
                              style={{
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: token.colorTextTertiary,
                                marginTop: 8,
                              }}
                            >
                              Loads
                            </Texto>
                            <Vertical gap={8}>
                              {Array.from({ length: maxLen }, (_, i) => (
                                <LoadCard
                                  key={i}
                                  loadIndex={i}
                                  beforeLoad={beforeLoads[i] || null}
                                  afterLoad={afterLoads[i] || null}
                                  changes={loadChangesMap.get(i) || []}
                                  fallbackProduct={dropProduct}
                                />
                              ))}
                            </Vertical>
                          </Vertical>
                        )
                      })()}
                    </Vertical>
                  )
                })
                .filter(Boolean)}

              {/* Note Section */}
              {hasNote && hasValue(beforeChange?.note, afterChange?.note) && (
                <Vertical gap={8}>
                  <Texto weight='600' style={{ fontSize: 13 }}>
                    Note
                  </Texto>
                  <ChangeRow
                    label='Note'
                    beforeValue={beforeChange?.note}
                    afterValue={afterChange?.note}
                  />
                </Vertical>
              )}
            </Vertical>
          </div>
        )}
      </Vertical>
    </Card>
  )
}

// Re-export types for consumers
export type { OrderChangeSummaryCardProps, ChangeSnapshot } from './types'
